import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { Button, Calendar, Fieldset, Input, Tooltip } from '@flixbus/honeycomb-react';
import { Icon, IconCalendar } from '@flixbus/honeycomb-icons-react';

import { useVoucherFormContext } from '../../../Context';
import { Asterisk } from '../../../Asterisk';

import { getNextYearDateOnly, toISODate } from '../../../../../helpers/dateTime';

const VoucherFormValidSince: React.FC<{ disabled: boolean; busPartner: boolean }> = ({
  disabled,
  busPartner,
}) => {
  const {
    formState: {
      payload: { validSince },
    },
    handlePayloadChange,
  } = useVoucherFormContext();

  const [calendarOpen, setCalendarOpen] = useState(false);

  const dateObject = validSince ? DateTime.fromISO(validSince) : null;
  const today = new Date().toISOString().split('T')[0];
  const maxDate = busPartner ? getNextYearDateOnly() : undefined;

  const disableDate = (date: Date) => {
    const min = new Date(today);
    min.setHours(0, 0, 0, 0);
    if (date < min) return true;
    if (maxDate) {
      const max = new Date(maxDate);
      max.setHours(23, 59, 59, 999);
      if (date > max) return true;
    }
    return false;
  };

  const calendarEndDate = maxDate
    ? new Date(maxDate)
    : new Date(new Date().setFullYear(new Date().getFullYear() + 5));

  return (
    <Fieldset itemSpacing='half'>
      <Tooltip
        id='calendar-validSince-tooltip'
        active={calendarOpen && !disabled}
        disableInternalHandlers
        onToggle={() => setCalendarOpen(false)}
        position='bottom'
        alignment='start'
        size='content-fit'
        wrapAround='block'
        extraClasses='calendar-tooltip'
        content={
          <Calendar
            id='calendar-validSince'
            appearance='compact'
            startDate={new Date(today)}
            endDate={calendarEndDate}
            selected={dateObject?.isValid ? dateObject.toJSDate() : undefined}
            showCurrentDate
            handleSelect={date => {
              const value = toISODate(DateTime.fromJSDate(date));
              handlePayloadChange({ value, prop: 'validSince' });
              setCalendarOpen(false);
            }}
            disableDayCondition={disableDate}
          />
        }
      >
        <Input
          name='date-field-validSince'
          id='date-field-validSince'
          label={<>Voucher valid from {!disabled && <Asterisk />}</>}
          type='text'
          readOnly
          disabled={disabled}
          required
          info={`The validity starts at 00:00 GMT${DateTime.local().toFormat('ZZ')}`}
          value={dateObject?.isValid ? dateObject.toFormat('dd.MM.yyyy') : ''}
          placeholder='DD.MM.YYYY'
          onClick={() => !disabled && setCalendarOpen(prev => !prev)}
          iconLeft={
            <Button
              aria-label='Open calendar'
              disabled={disabled}
              onClick={() => setCalendarOpen(prev => !prev)}
            >
              <Icon appearance='primary' InlineIcon={IconCalendar} />
            </Button>
          }
        />
      </Tooltip>
    </Fieldset>
  );
};

export default VoucherFormValidSince;
